import {
  Note,
  Evaluation,
} from '@teetsh/app/src/containers/CarnetsDeNotes/types/carnetsDeNotes';
import { NotationSystem } from '@teetsh/app/src/types/notationSystem';
import {
  getEvaluationStructure,
  createNotesForStudent,
} from './noteCreationUtils';
import { findItemFromBOById } from '@teetsh/app/src/containers/CarnetsDeNotes/Evaluations/findItemFromBOById';

interface EnsureNotesOptions {
  /**
   * The evaluation object, needed when enrichment is required
   */
  evaluation?: Evaluation;
  /**
   * Whether to add enrichment data (evaluationName, referentialItemValue)
   */
  includeEnrichment?: boolean;
  notationSystem?: NotationSystem;
}

function areAllNoteWithReferentialId(notes: Note[]): boolean {
  return notes.every(note => note.referentialItemId !== undefined);
}

function enrichNotes(
  notes: Note[],
  evaluation?: Evaluation,
  includeEnrichment?: boolean,
): Note[] {
  if (!includeEnrichment || !evaluation) {
    return notes;
  }

  return notes.map(note => ({
    ...note,
    evaluationId: evaluation.id,
    evaluationName: evaluation.name,
    referentialItemValue: note.referentialItemId
      ? findItemFromBOById(note.referentialItemId, evaluation.referential)
          ?.value
      : undefined,
  }));
}

/**
 * Ensures a student has notes for an evaluation, creating them if missing.
 */
export function ensureNotesForStudent(
  eleveId: string,
  evaluationId: string,
  existingNotes: Note[],
  options: EnsureNotesOptions = {},
): Note[] {
  const { evaluation, includeEnrichment = false, notationSystem } = options;

  // Find existing notes for this student
  const studentNotes = existingNotes.filter(note => note.eleveId === eleveId);

  // If student already has notes, return them (possibly enriched)
  if (studentNotes.length > 0) {
    // Special case: handle mixed referential situations
    if (studentNotes.length > 1 && !areAllNoteWithReferentialId(studentNotes)) {
      const notesWithNoReferential = studentNotes.filter(
        note => !note.referentialItemId,
      );
      const noteToKeep =
        notesWithNoReferential.find(note => note.note !== undefined) ||
        notesWithNoReferential[0];

      return enrichNotes([noteToKeep], evaluation, includeEnrichment);
    }

    return enrichNotes(studentNotes, evaluation, includeEnrichment);
  }

  // No existing notes - create new ones based on evaluation structure
  const evaluationStructure = getEvaluationStructure(existingNotes);
  const newNotes = createNotesForStudent(
    eleveId,
    evaluationId,
    evaluationStructure,
    notationSystem,
  );

  return enrichNotes(newNotes, evaluation, includeEnrichment);
}

/**
 * Ensures all students in a list have notes for an evaluation
 */
export function ensureNotesForAllStudents(
  eleveIds: string[],
  evaluationId: string,
  existingNotes: Note[],
  options: EnsureNotesOptions = {},
): Note[] {
  const allNotes: Note[] = [];

  for (const eleveId of eleveIds) {
    const studentNotes = ensureNotesForStudent(
      eleveId,
      evaluationId,
      existingNotes,
      options,
    );
    allNotes.push(...studentNotes);
  }

  return allNotes;
}
